import React, { useMemo, useState } from 'react';

import { formatMonthLabel } from './chartUtils';

const CHART_HEIGHT = 200;
const PADDING = { top: 16, right: 12, bottom: 36, left: 36 };

const ActivityChart = ({ monthly, peakMonth, avgPerMonth }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const layout = useMemo(() => {
    if (!monthly.length) return null;

    const maxCount = Math.max(...monthly.map((m) => m.count), 1);
    const barGap = monthly.length > 18 ? 4 : 8;
    const minBarWidth = 28;
    const innerWidth = Math.max(
      monthly.length * (minBarWidth + barGap),
      320
    );
    const width = innerWidth + PADDING.left + PADDING.right;
    const height = CHART_HEIGHT + PADDING.top + PADDING.bottom;
    const plotHeight = CHART_HEIGHT;

    const yTicks = [0, Math.ceil(maxCount / 2), maxCount].filter(
      (v, i, arr) => i === 0 || v !== arr[i - 1]
    );

    const bars = monthly.map((point, index) => {
      const barHeight = (point.count / maxCount) * plotHeight;
      const x =
        PADDING.left + index * (minBarWidth + barGap) + barGap / 2;
      const y = PADDING.top + plotHeight - barHeight;

      return {
        ...point,
        index,
        x,
        y,
        width: minBarWidth,
        height: barHeight,
        label: formatMonthLabel(point.month),
      };
    });

    return { width, height, plotHeight, maxCount, yTicks, bars, barGap };
  }, [monthly]);

  if (!layout) {
    return <p className="analytics-empty">No date data available</p>;
  }

  const hovered = hoveredIndex != null ? layout.bars[hoveredIndex] : null;

  return (
    <div className="activity-chart">
      <div className="activity-chart-stats">
        <div className="activity-stat">
          <span className="activity-stat-value">{peakMonth?.count ?? '—'}</span>
          <span className="activity-stat-label">
            Peak month
            {peakMonth && (
              <span className="activity-stat-detail">
                {' '}
                · {formatMonthLabel(peakMonth.month)}
              </span>
            )}
          </span>
        </div>
        <div className="activity-stat">
          <span className="activity-stat-value">{avgPerMonth}</span>
          <span className="activity-stat-label">Avg per month</span>
        </div>
        <div className="activity-stat">
          <span className="activity-stat-value">{monthly.length}</span>
          <span className="activity-stat-label">Months tracked</span>
        </div>
        {hovered && (
          <div className="activity-stat activity-stat-hover">
            <span className="activity-stat-value">{hovered.count}</span>
            <span className="activity-stat-label">{hovered.label}</span>
          </div>
        )}
      </div>

      <div className="activity-chart-scroll">
        <svg
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          className="activity-chart-svg"
          preserveAspectRatio="xMinYMid meet"
          role="img"
          aria-label="Additions per month"
        >
          {layout.yTicks.map((tick) => {
            const y =
              PADDING.top +
              layout.plotHeight -
              (tick / layout.maxCount) * layout.plotHeight;
            return (
              <g key={tick} className="activity-grid-line">
                <line
                  x1={PADDING.left}
                  y1={y}
                  x2={layout.width - PADDING.right}
                  y2={y}
                />
                <text
                  x={PADDING.left - 8}
                  y={y + 4}
                  textAnchor="end"
                  className="activity-axis-label"
                >
                  {tick}
                </text>
              </g>
            );
          })}

          {layout.bars.map((bar) => (
            <g
              key={bar.month}
              onMouseEnter={() => setHoveredIndex(bar.index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <rect
                x={bar.x}
                y={bar.y}
                width={bar.width}
                height={Math.max(bar.height, 2)}
                rx={4}
                className={`activity-bar ${
                  hoveredIndex === bar.index ? 'activity-bar-active' : ''
                } ${bar.month === peakMonth?.month ? 'activity-bar-peak' : ''}`}
              />
              <text
                x={bar.x + bar.width / 2}
                y={layout.height - 10}
                textAnchor="middle"
                className="activity-month-label"
                transform={
                  layout.bars.length > 12
                    ? `rotate(-40, ${bar.x + bar.width / 2}, ${layout.height - 10})`
                    : undefined
                }
              >
                {bar.label}
              </text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
};

export default ActivityChart;
